import { create } from "zustand";
import {
  GroupAdminInfo,
  GroupResource,
  CreateGroupRequest,
  GroupUserRole,
} from "../api/groups";

interface GroupsState {
  groups: GroupAdminInfo[];
  loading: boolean;
  error: string | null;
  loadGroups: () => Promise<void>;
  loadGroupDetail: (groupId: string) => Promise<GroupAdminInfo>;
  createGroup: (data: CreateGroupRequest) => Promise<GroupAdminInfo>;
  deleteGroup: (groupId: string) => Promise<void>;
  addMember: (groupId: string, userId: string, role: GroupUserRole) => Promise<GroupAdminInfo>;
  removeMember: (groupId: string, userId: string) => Promise<GroupAdminInfo>;
}

export const useGroupsStore = create<GroupsState>((set, get) => ({
  groups: [],
  loading: false,
  error: null,

  loadGroups: async () => {
    set({ loading: true, error: null });
    try {
      const groups = await GroupResource.getList();
      set({ groups, loading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to load groups",
        loading: false,
      });
    }
  },

  loadGroupDetail: async (groupId) => {
    // The list returns a lightweight roster (count + name only). The detail view
    // fetches the fully-enriched group (email + account state) and upserts it.
    const full = await GroupResource.getById(groupId);
    set((state) => ({
      groups: state.groups.some((g) => g.id === groupId)
        ? state.groups.map((g) => (g.id === groupId ? full : g))
        : [...state.groups, full],
    }));
    return full;
  },

  createGroup: async (data) => {
    const group = await GroupResource.create(data);
    set((state) => ({ groups: [...state.groups, group] }));
    return group;
  },

  deleteGroup: async (groupId) => {
    await GroupResource.delete(groupId);
    set((state) => ({
      groups: state.groups.filter((g) => g.id !== groupId),
    }));
  },

  addMember: async (groupId, userId, role) => {
    const updated = await GroupResource.addMember(groupId, userId, role);
    set((state) => ({
      groups: state.groups.map((g) => (g.id === groupId ? updated : g)),
    }));
    return updated;
  },

  removeMember: async (groupId, userId) => {
    const updated = await GroupResource.removeMember(groupId, userId);
    set((state) => ({
      groups: state.groups.map((g) => (g.id === groupId ? updated : g)),
    }));
    return updated;
  },
}));
